import type { NdtIndicationRow } from '../components/dashboard/drilldown/drillDownTypes';
import {
  fetchNdtIndicationLogsApi,
  type FetchNdtLogsParams,
} from './fetchNdtIndicationLogsApi';

const NDT_PAGE_SIZE = 1000;
const NDT_MAX_PAGES = 40;

export interface NdtRowRef {
  key: string;
  weldNumber: string;
  jobNumber: string;
  pipeId?: number;
}

function pageLooksRepeated(previous: NdtIndicationRow[], next: NdtIndicationRow[]): boolean {
  if (!previous.length || !next.length) return false;
  const prevId = previous[0]?.id;
  const nextId = next[0]?.id;
  return prevId != null && nextId != null && prevId === nextId;
}

async function fetchScope(params: FetchNdtLogsParams): Promise<NdtIndicationRow[]> {
  const all: NdtIndicationRow[] = [];
  for (let page = 0; page < NDT_MAX_PAGES; page += 1) {
    const logs = await fetchNdtIndicationLogsApi({
      ...params,
      limit: NDT_PAGE_SIZE,
      offset: page * NDT_PAGE_SIZE,
    });
    if (pageLooksRepeated(all, logs)) break;
    all.push(...logs);
    if (logs.length < NDT_PAGE_SIZE) break;
  }
  return all;
}

/** One paged request per pipe, instead of one request per weld. */
export async function fetchNdtLogsForPipes(
  projectId: number,
  pipeIds: number[],
): Promise<NdtIndicationRow[]> {
  const scopes: FetchNdtLogsParams[] = pipeIds.length
    ? pipeIds.map((pipeId) => ({ project_id: projectId, pipe_id: pipeId }))
    : [{ project_id: projectId }];
  const chunks = await Promise.all(scopes.map((scope) => fetchScope(scope)));
  return chunks.flat();
}

export function logsHaveWeldIdentity(logs: NdtIndicationRow[]): boolean {
  return logs.some((log) => String(log.weldNumber ?? '').trim().length > 0);
}

/** Bucket project-level logs onto drill-down rows. Unmatched rows get an empty list. */
export function indexNdtLogsForRows(
  rows: NdtRowRef[],
  logs: NdtIndicationRow[],
): Record<string, NdtIndicationRow[]> {
  const byWeld = new Map<string, NdtIndicationRow[]>();
  logs.forEach((log) => {
    const weld = String(log.weldNumber ?? '').trim();
    if (!weld) return;
    const list = byWeld.get(weld) ?? [];
    list.push(log);
    byWeld.set(weld, list);
  });

  const indexed: Record<string, NdtIndicationRow[]> = {};
  rows.forEach((row) => {
    const weld = row.weldNumber.trim();
    const job = row.jobNumber.trim();
    const candidates = byWeld.get(weld) ?? [];
    indexed[row.key] = job
      ? candidates.filter((log) => {
          const logJob = String(log.jobNumber ?? '').trim();
          return !logJob || logJob === job;
        })
      : candidates;
  });
  return indexed;
}
